/**
 * Netxol One translations (react-i18next). English is the master; Spanish (es-ES), Italian and Arabic are typed against it.
 * Language order: the user's own choice (saved on their account) → the company default → English.
 * The subscriber portal uses the subscriber's language instead (saved on the customer record).
 *
 * Each language is its own chunk, loaded on demand: a user downloads only the language they read. English (the
 * fallback) is fetched only when the language is English, is partial (Urdu), or a key turns out to be missing.
 * This module waits (top-level await) for the language before anything that imports it runs, so every screen and
 * every module-level t() call is translated from the first paint.
 */
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';

export const LANGS = ['en', 'es', 'it', 'ar'] as const;
export type Lang = (typeof LANGS)[number];
/** The subscriber portal also speaks Urdu — on its support and package-change screens (the rest falls back to English). */
export const PORTAL_LANGS = [...LANGS, 'ur'] as const;
export type PortalLang = (typeof PORTAL_LANGS)[number];
/** The subscriber's pages (portal, the public help centre, survey answers) also speak Urdu. */
const onPortal = () => /^\/(portal|help|survey)(\/|$)/.test(location.pathname);
const allowed = (l: string): l is Lang => (LANGS as readonly string[]).includes(l) || (l === 'ur' && onPortal());
// Arabic (Saudi Arabia) with the Gregorian calendar and Western digits, as in the ISP's documents and most Saudi business software
const INTL: Record<PortalLang, string> = { en: 'en-GB', es: 'es-ES', it: 'it-IT', ar: 'ar-SA-u-ca-gregory-nu-latn', ur: 'ur-PK-u-nu-latn' };
/** Right-to-left languages: the whole console mirrors (logical CSS start/end). */
export const isRtl = (l: string) => l === 'ar' || l === 'ur';

type Bundle = Record<string, unknown>;
const LOADERS: Record<PortalLang, () => Promise<{ default: Bundle }>> = {
  en: () => import('./locales/en'),
  es: () => import('./locales/es'),
  it: () => import('./locales/it'),
  ar: () => import('./locales/ar'),
  ur: () => import('./locales/ur'),
};
/** Languages that hold only some screens: English is loaded with them as the fallback. */
const PARTIAL = new Set<PortalLang>(['ur']);
const bundles = new Map<PortalLang, Promise<Bundle>>();
const fetchBundle = (l: PortalLang): Promise<Bundle> => {
  let p = bundles.get(l);
  if (!p) { p = LOADERS[l]().then((m) => m.default); p.catch(() => bundles.delete(l)); bundles.set(l, p); }
  return p;
};
/** The bundles a language needs: itself, plus English when it is partial. */
const needFor = (l: PortalLang): PortalLang[] => (PARTIAL.has(l) ? [l, 'en'] : [l]);
/** Make a language available to i18next. */
async function loadLanguage(l: PortalLang) {
  const need = needFor(l);
  const data = await Promise.all(need.map(fetchBundle));
  need.forEach((n, i) => { if (!i18n.hasResourceBundle(n, 'translation')) i18n.addResourceBundle(n, 'translation', data[i], true, true); });
}

const browser = (navigator.language || 'en').slice(0, 2) as Lang;
const stored = (localStorage.getItem('nx.lang') as Lang | null) ?? (location.pathname.startsWith('/blocked') && LANGS.includes(browser) ? browser : undefined);
let first: PortalLang = stored && allowed(stored) ? stored : 'en';
const initial: Record<string, { translation: Bundle }> = {};
try {
  const need = needFor(first);
  (await Promise.all(need.map(fetchBundle))).forEach((b, i) => { initial[need[i]] = { translation: b }; });
} catch {
  first = 'en';   // the language file could not be fetched: show English rather than nothing
  initial.en = { translation: await fetchBundle('en') };
}
await i18n.use(initReactI18next).init({
  resources: initial,
  lng: first,
  fallbackLng: 'en',
  interpolation: { escapeValue: false },
  returnNull: false,
  // a key the language lacks (and the call gives no default text): fetch English once; components re-render when it arrives
  saveMissing: true,
  missingKeyHandler: (_lngs: readonly string[], _ns: string, _key: string, _fallback: string, _update: boolean, opts?: { defaultValue?: unknown }) => {
    if (opts?.defaultValue === undefined && !i18n.hasResourceBundle('en', 'translation')) void loadLanguage('en').catch(() => undefined);
  },
  react: { bindI18nStore: 'added' },
});
document.documentElement.lang = i18n.language;
document.documentElement.dir = isRtl(i18n.language) ? 'rtl' : 'ltr';

/**
 * Switch language. Like SAP Fiori or Salesforce, the app reloads in the new language so every screen, table header
 * and chart label is rebuilt consistently (no half-translated state).
 */
export function setLanguage(l: Lang | PortalLang, opts: { reload?: boolean } = {}) {
  if (!allowed(l)) return;
  const changed = i18n.language !== l;
  localStorage.setItem('nx.lang', l);
  document.documentElement.lang = l;
  document.documentElement.dir = isRtl(l) ? 'rtl' : 'ltr';
  if (!changed) return;
  if (opts.reload) location.reload();
  else void loadLanguage(l).then(() => i18n.changeLanguage(l)).catch(() => location.reload());
}
/** The language the console is shown in now. */
export const getLanguage = (): Lang => (LANGS.includes(i18n.language as Lang) ? i18n.language as Lang : 'en');
/** True while the console is shown right-to-left (Arabic). */
export const rtlNow = () => isRtl(getLanguage());
/** BCP-47 locale for Intl formatting of the current language. */
export const intlLocale = () => INTL[(i18n.language as PortalLang) ?? 'en'] ?? 'en-GB';
export const useT = () => useTranslation().t;
/** Translate outside React components (tables, column definitions, toasts). */
export const t = (key: string, opts?: Record<string, unknown>): string => i18n.t(key, opts as any) as unknown as string;
export default i18n;
